import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { XCircle, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import {
  consumeSlickPayReturnOrigin,
  consumePendingSlickPayOrder,
  slickPayInvoiceOriginKey,
  slickPayCreditsOriginKey,
  type SlickPayResultState,
} from '@/lib/slickpay-return';

type CallbackStatus = 'processing' | 'not_found' | 'error';

/**
 * SlickPay's own redirect status, confirmed by Hope Macy: appended as `status`
 * alongside `po` (the poId) on every return. `success` means the bank
 * confirmed the payment; the rest are all terminal failures where either no
 * attempt was made (`denied`) or the attempt failed (`rejected`, `funds`),
 * or SlickPay itself couldn't resolve the order (`notFound`, `error`).
 */
type SlickPayRedirectStatus = 'success' | 'notFound' | 'denied' | 'error' | 'rejected' | 'funds';

const NON_SUCCESS_MESSAGES: Record<Exclude<SlickPayRedirectStatus, 'success'>, string> = {
  notFound: 'We could not find this payment with your bank. If you completed a payment, check your dashboard — it will update automatically.',
  denied: 'Payment cancelled. No money was taken from your account.',
  error: 'Something went wrong processing your payment with your bank. If money left your account, check your dashboard — it updates automatically.',
  rejected: 'Your bank rejected this payment. Please try again or use a different payment method.',
  funds: 'Payment failed due to insufficient funds. Please try again once funds are available.',
};

function parseRedirectStatus(raw: string | null): SlickPayRedirectStatus | null {
  const allowed: SlickPayRedirectStatus[] = ['success', 'notFound', 'denied', 'error', 'rejected', 'funds'];
  return allowed.includes(raw as SlickPayRedirectStatus) ? (raw as SlickPayRedirectStatus) : null;
}

interface ResolveOrderContextResult {
  found: boolean;
  type?: 'invoice' | 'credit_purchase' | 'payment_link';
  unitId?: string;
  invoiceId?: string;
  linkId?: string;
  paymentId?: string;
}

interface InvoiceCallbackResult {
  success?: boolean;
  status?: string;
  transactionId?: string;
  message?: string;
}

interface CreditCallbackResult {
  success?: boolean;
  status?: string;
  credits?: number;
  message?: string;
}

const POLL_INTERVAL_MS = 4000;

/**
 * SlickPay already told us the outcome (`denied`/`rejected`/`funds`/`notFound`/`error`)
 * via the redirect, so a handful of attempts is enough for our backend/PIS to
 * catch up before we just trust SlickPay's own verdict and stop. Only the
 * `success` case polls indefinitely (there the bank has confirmed, but our
 * backend still needs to settle via PIS before crediting).
 */
const MAX_BOUNDED_POLL_ATTEMPTS = 3;

/**
 * Single SlickPay PIS v1 return URL shared by every Open Banking checkout
 * flow (invoice payments and unit credit purchases). Hope Macy's return URL
 * is fixed at the application level, so this page first resolves which flow
 * the incoming poId belongs to, then polls the matching backend callback
 * until a terminal status, and redirects back to the page where checkout
 * was started (with a result toast via location.state).
 */
export default function SlickPayReturnPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [backLink, setBackLink] = useState<string>('/dashboard');
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const redirectToOrigin = (originKey: string, fallback: string, result: SlickPayResultState) => {
    const origin = consumeSlickPayReturnOrigin(originKey, fallback);
    navigate(origin, { replace: true, state: result });
  };

  const scheduleRetry = (fn: () => void) => {
    if (cancelledRef.current) return;
    setStatus('processing');
    setErrorMessage('Confirming payment…');
    retryTimeoutRef.current = setTimeout(fn, POLL_INTERVAL_MS);
  };

  /** True once a bounded (non-success) redirect status has exhausted its attempts. */
  const isExhausted = (redirectStatus: SlickPayRedirectStatus | null, attempt: number): boolean =>
    redirectStatus !== null && redirectStatus !== 'success' && attempt >= MAX_BOUNDED_POLL_ATTEMPTS;

  const pollInvoice = async (
    invoiceId: string,
    poId: string,
    redirectStatus: SlickPayRedirectStatus | null,
    attempt = 0,
  ) => {
    if (cancelledRef.current) return;
    const originKey = slickPayInvoiceOriginKey(invoiceId);
    const fallback = `/payment/${invoiceId}`;
    setBackLink(fallback);

    const giveUpWithRedirectStatus = () => {
      if (redirectStatus && redirectStatus !== 'success') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: NON_SUCCESS_MESSAGES[redirectStatus],
        });
      } else {
        scheduleRetry(() => void pollInvoice(invoiceId, poId, redirectStatus, attempt + 1));
      }
    };

    try {
      const handleCallback = httpsCallable<
        { invoiceId: string; slickpayInvoiceId: string },
        InvoiceCallbackResult
      >(functions, 'handleSlickPayCallback');

      const result = await handleCallback({ invoiceId, slickpayInvoiceId: poId });
      if (cancelledRef.current) return;
      const data = result.data;

      if (data.success && data.status === 'PAID') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'success',
          message: 'Your Open Banking payment has been confirmed.',
        });
        return;
      }
      if (data.status === 'REJECTED') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'Your bank declined this payment. Please try again or use a different payment method.',
        });
        return;
      }
      if (data.status === 'EXPIRED') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'This payment session expired. Start a new payment to continue.',
        });
        return;
      }
      if (data.status === 'VOIDED') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'This payment request was cancelled. Please contact your unit secretary if you believe this is an error.',
        });
        return;
      }
      if (data.status === 'MISMATCH') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'Payment reference mismatch. Please check your dashboard for the latest status.',
        });
        return;
      }
      if (data.status === 'NOT_FOUND') {
        if (redirectStatus && redirectStatus !== 'success') {
          redirectToOrigin(originKey, fallback, {
            slickpayResult: 'failed',
            message: NON_SUCCESS_MESSAGES[redirectStatus],
          });
        } else {
          setStatus('not_found');
        }
        return;
      }

      // PENDING / UNKNOWN — keep polling, unless SlickPay already told us this
      // was a non-success outcome and we've given it enough attempts to sync.
      if (isExhausted(redirectStatus, attempt + 1)) {
        giveUpWithRedirectStatus();
        return;
      }
      scheduleRetry(() => void pollInvoice(invoiceId, poId, redirectStatus, attempt + 1));
    } catch (error: unknown) {
      if (cancelledRef.current) return;
      const err = error as { code?: string };
      if (err.code === 'functions/not-found') {
        if (redirectStatus && redirectStatus !== 'success') {
          redirectToOrigin(originKey, fallback, {
            slickpayResult: 'failed',
            message: NON_SUCCESS_MESSAGES[redirectStatus],
          });
        } else {
          setStatus('not_found');
        }
        return;
      }
      if (err.code === 'functions/unauthenticated') {
        setStatus('error');
        setErrorMessage('You need to be signed in to confirm this payment.');
        return;
      }
      if (err.code === 'functions/permission-denied') {
        setStatus('error');
        setErrorMessage('You do not have permission to confirm this payment.');
        return;
      }
      // Transient / unexpected errors — keep polling (bounded as above)
      if (isExhausted(redirectStatus, attempt + 1)) {
        giveUpWithRedirectStatus();
        return;
      }
      scheduleRetry(() => void pollInvoice(invoiceId, poId, redirectStatus, attempt + 1));
    }
  };

  const pollCredits = async (
    unitId: string,
    poId: string,
    redirectStatus: SlickPayRedirectStatus | null,
    attempt = 0,
  ) => {
    if (cancelledRef.current) return;
    const originKey = slickPayCreditsOriginKey(unitId);
    const fallback = `/unit/${unitId}/credits`;
    setBackLink(fallback);

    const giveUpWithRedirectStatus = () => {
      if (redirectStatus && redirectStatus !== 'success') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: NON_SUCCESS_MESSAGES[redirectStatus],
        });
      } else {
        scheduleRetry(() => void pollCredits(unitId, poId, redirectStatus, attempt + 1));
      }
    };

    try {
      const handleCallback = httpsCallable<
        { unitId: string; poId: string },
        CreditCallbackResult
      >(functions, 'handleCreditPurchaseSlickPayCallback');

      const result = await handleCallback({ unitId, poId });
      if (cancelledRef.current) return;
      const data = result.data;

      if (data.success && data.status === 'PAID') {
        const creditsMsg =
          typeof data.credits === 'number'
            ? `${data.credits} credits have been added to your unit balance.`
            : 'Credits have been added to your unit balance.';
        redirectToOrigin(originKey, fallback, { slickpayResult: 'success', message: creditsMsg });
        return;
      }
      if (data.status === 'REJECTED') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'Your bank declined this payment. Please go back and try again.',
        });
        return;
      }
      if (data.status === 'EXPIRED') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'This payment session expired. Please start a new credit purchase.',
        });
        return;
      }
      if (data.status === 'VOIDED') {
        redirectToOrigin(originKey, fallback, {
          slickpayResult: 'failed',
          message: 'This payment request was cancelled. Please start a new purchase if you still need credits.',
        });
        return;
      }
      if (data.status === 'NOT_FOUND') {
        if (redirectStatus && redirectStatus !== 'success') {
          redirectToOrigin(originKey, fallback, {
            slickpayResult: 'failed',
            message: NON_SUCCESS_MESSAGES[redirectStatus],
          });
        } else {
          setStatus('not_found');
        }
        return;
      }

      // PENDING / UNKNOWN — keep polling, unless SlickPay already told us this
      // was a non-success outcome and we've given it enough attempts to sync.
      if (isExhausted(redirectStatus, attempt + 1)) {
        giveUpWithRedirectStatus();
        return;
      }
      scheduleRetry(() => void pollCredits(unitId, poId, redirectStatus, attempt + 1));
    } catch (error: unknown) {
      if (cancelledRef.current) return;
      const err = error as { code?: string };
      if (err.code === 'functions/not-found') {
        if (redirectStatus && redirectStatus !== 'success') {
          redirectToOrigin(originKey, fallback, {
            slickpayResult: 'failed',
            message: NON_SUCCESS_MESSAGES[redirectStatus],
          });
        } else {
          setStatus('not_found');
        }
        return;
      }
      if (err.code === 'functions/unauthenticated') {
        setStatus('error');
        setErrorMessage('You need to be signed in to confirm this payment.');
        return;
      }
      if (err.code === 'functions/permission-denied') {
        setStatus('error');
        setErrorMessage('Only a Secretary or Treasurer may confirm credit purchases for this unit.');
        return;
      }
      // Transient / unexpected errors — keep polling (bounded as above)
      if (isExhausted(redirectStatus, attempt + 1)) {
        giveUpWithRedirectStatus();
        return;
      }
      scheduleRetry(() => void pollCredits(unitId, poId, redirectStatus, attempt + 1));
    }
  };

  useEffect(() => {
    cancelledRef.current = false;

    const start = async () => {
      // SlickPay/Hope Macy's actual return params are `po` and `status`. The
      // rest are kept as fallbacks for older links / manual testing.
      const poId =
        searchParams.get('po') ||
        searchParams.get('slickpay_invoice_id') ||
        searchParams.get('po_id') ||
        searchParams.get('poId') ||
        searchParams.get('invoice_id') ||
        searchParams.get('id') ||
        consumePendingSlickPayOrder();

      const redirectStatus = parseRedirectStatus(searchParams.get('status'));

      if (!poId) {
        if (redirectStatus && redirectStatus !== 'success') {
          setStatus('error');
          setErrorMessage(NON_SUCCESS_MESSAGES[redirectStatus]);
        } else {
          setStatus('not_found');
        }
        return;
      }

      try {
        const resolveContext = httpsCallable<{ poId: string }, ResolveOrderContextResult>(
          functions,
          'resolveSlickPayOrderContext',
        );
        const result = await resolveContext({ poId });
        if (cancelledRef.current) return;
        const context = result.data;

        if (context.found && context.type === 'invoice' && context.invoiceId) {
          // SlickPay already confirmed no attempt was made — nothing for PIS
          // to ever settle, so skip straight to the failure toast.
          if (redirectStatus === 'denied') {
            redirectToOrigin(slickPayInvoiceOriginKey(context.invoiceId), `/payment/${context.invoiceId}`, {
              slickpayResult: 'failed',
              message: NON_SUCCESS_MESSAGES.denied,
            });
            return;
          }
          await pollInvoice(context.invoiceId, poId, redirectStatus);
          return;
        }
        if (context.found && context.type === 'payment_link' && context.unitId && context.linkId && context.paymentId) {
          // Payment links have their own confirmation page, which polls until the bank settles.
          const query = new URLSearchParams({ pid: context.paymentId });
          if (redirectStatus && redirectStatus !== 'success') query.set('bank', redirectStatus);
          navigate(
            `/pay/${encodeURIComponent(context.unitId)}/${encodeURIComponent(context.linkId)}/complete?${query.toString()}`,
            { replace: true },
          );
          return;
        }
        if (context.found && context.type === 'credit_purchase' && context.unitId) {
          if (redirectStatus === 'denied') {
            redirectToOrigin(slickPayCreditsOriginKey(context.unitId), `/unit/${context.unitId}/credits`, {
              slickpayResult: 'failed',
              message: NON_SUCCESS_MESSAGES.denied,
            });
            return;
          }
          await pollCredits(context.unitId, poId, redirectStatus);
          return;
        }

        if (redirectStatus && redirectStatus !== 'success') {
          setStatus('error');
          setErrorMessage(NON_SUCCESS_MESSAGES[redirectStatus]);
        } else {
          setStatus('not_found');
        }
      } catch {
        if (cancelledRef.current) return;
        if (redirectStatus && redirectStatus !== 'success') {
          setStatus('error');
          setErrorMessage(NON_SUCCESS_MESSAGES[redirectStatus]);
        } else {
          setStatus('not_found');
        }
      }
    };

    void start();

    return () => {
      cancelledRef.current = true;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-4">
            {status === 'processing' && (
              <>
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Processing payment</CardTitle>
              </>
            )}
            {(['not_found', 'error'] as CallbackStatus[]).includes(status) && (
              <>
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
                <CardTitle className="text-2xl text-red-900">Something went wrong</CardTitle>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            {status === 'processing' && (
              <p className="text-muted-foreground">
                {errorMessage || 'Please wait while we confirm your Open Banking payment…'}
              </p>
            )}
            {status === 'not_found' && (
              <>
                <p className="text-sm text-muted-foreground">
                  Payment reference not found. If you completed a payment, check your dashboard — it will update automatically once confirmed.
                </p>
                <Button variant="outline" onClick={() => navigate(backLink)}>
                  Go to Dashboard
                </Button>
              </>
            )}
            {status === 'error' && (
              <>
                <p className="text-sm text-red-800">
                  {errorMessage || 'Something went wrong confirming your payment. If money left your account, check your dashboard — it updates automatically.'}
                </p>
                <Button variant="outline" onClick={() => navigate(backLink)}>
                  Go to Dashboard
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
